import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
// Varsayılan dil (tr) senkron gömülür: hydration anında çeviriler hazır olmazsa
// t() kısa süreliğine anahtar döndürüp SSR metnini siler; bu, hero'da çift
// layout shift ve LCP gecikmesi yaratıyordu. en/ru lazy yüklenmeye devam eder.
import trResources from '../public/locales/tr/tr.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'ru', label: 'Русский', flag: '🇷🇺' },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const NAMESPACES = [
  'common',
  'home',
  'leagues',
  'clubs',
  'tournaments',
  'trainings',
  'courts',
  'profile',
  'match',
  'auth',
  'info',
  'prizes',
  'pricing',
  'academy',
  'discover',
] as const;

const isClient = typeof window !== 'undefined';

if (!i18n.isInitialized) {
  const chain = i18n
    .use(
      resourcesToBackend(async (language: string, namespace: string) => {
        // Import JSON directly — no HTTP request, works in SSR and CSR
        const resources = await import(`../public/locales/${language}/${language}.json`);
        return resources.default?.[namespace] ?? resources[namespace] ?? {};
      }),
    )
    .use(initReactI18next);

  if (isClient) chain.use(LanguageDetector);

  chain.init({
    resources: { tr: trResources as never },
    partialBundledLanguages: true,
    fallbackLng: 'tr',
    supportedLngs: ['tr', 'en', 'ru'],
    ns: [...NAMESPACES],
    defaultNS: 'common',
    fallbackNS: 'common',
    detection: {
      // 'navigator' bilinçli olarak dışarıda: SSR her zaman tr üretir; tarayıcı
      // diline göre hydration sonrası dil değişimi hero metnini yeniden akıtıp
      // LCP'yi ~2.8s geciktiriyor ve CLS üretiyordu. Dil, kullanıcı seçimiyle
      // (cookie/localStorage) belirlenir.
      order: ['cookie', 'localStorage'],
      caches: ['cookie', 'localStorage'],
      lookupCookie: 'lang',
      lookupLocalStorage: 'lang',
    },
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });

  // <html lang> drives screen-reader pronunciation and CSS uppercase (i/İ); keep it in sync
  // with the chosen language. SSR always renders tr (pages/_document.tsx).
  if (isClient) {
    const syncLang = (lng: string) => {
      document.documentElement.lang = lng?.split('-')[0] || 'tr';
    };
    if (i18n.language) syncLang(i18n.language);
    i18n.on('languageChanged', syncLang);
  }
}

export default i18n;
